import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '../app/store/useAppStore';
import { Icon } from '../components/ui/Icon';
import { CameraHud } from '../components/hud/CameraHud';
import { CountdownEffects } from '../components/camera/CountdownEffects';
import { useCamera } from '../features/camera/useCamera';
import { useGeolocation } from '../features/geolocation/useGeolocation';
import { useColorAnalysis } from '../features/color-analysis/useColorAnalysis';
import { useFaceTracking } from '../features/face-tracking/useFaceTracking';
import { buildEncounter } from '../features/encounter/encounter';
import { getCaptureModule } from '../modules/capture/registry';
import { archiveRepository } from '../db/gcam-db';
import type { EncounterRecord, RoiRect } from '../types';
import type { CountdownStage } from '../modules/capture/types';

type CapturePhase = 'idle' | 'countdown' | 'capturing' | 'processing';

const countdownLabels: Record<CountdownStage['effect'], string> = {
  particle: 'POINT FIELD / 03',
  thermal: 'SPECTRAL DIVIDE / 02',
  dither: 'DITHER LOCK / 01',
  flash: 'EXPOSURE / G',
};

const countdownCells = Array.from({ length: 20 }, (_, index) => index);

function waitFor(duration: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, duration));
}

function navigateWithViewTransition(update: () => void) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const viewTransitionDocument = document as Document & {
    startViewTransition?: (callback: () => void) => { finished: Promise<void> };
  };
  if (!reducedMotion && viewTransitionDocument.startViewTransition) {
    viewTransitionDocument.startViewTransition(update);
    return;
  }
  update();
}

export function CameraPage() {
  const settings = useAppStore((state) => state.settings);
  const updateSettings = useAppStore((state) => state.updateSettings);
  const navigate = useAppStore((state) => state.navigate);
  const openOverlay = useAppStore((state) => state.openOverlay);
  const setEncounter = useAppStore((state) => state.setEncounter);
  const setNotice = useAppStore((state) => state.setNotice);
  const activeModuleId = useAppStore((state) => state.activeCaptureModuleId);
  const camera = useCamera(settings);
  const { location } = useGeolocation();
  const [capturePhase, setCapturePhase] = useState<CapturePhase>('idle');
  const captureActive = capturePhase !== 'idle';
  const palette = useColorAnalysis(camera.videoRef, settings, camera.status === 'ready' && !captureActive);
  const { faces, status: faceStatus } = useFaceTracking(camera.videoRef, settings.showFaceHud && camera.status === 'ready' && !captureActive, settings.guestName);
  const [countdown, setCountdown] = useState<CountdownStage | null>(null);
  const [lastRecord, setLastRecord] = useState<EncounterRecord | null>(null);
  const roiStart = useRef<{ x: number; y: number; roi: RoiRect; mode: 'move' | 'resize' } | null>(null);
  const captureBusyRef = useRef(false);
  const captureRunRef = useRef(0);

  useEffect(() => { void archiveRepository.list().then((records) => setLastRecord(records[0] ?? null)); }, []);
  useEffect(() => () => { captureRunRef.current += 1; captureBusyRef.current = false; }, []);

  const runCapture = useCallback(async () => {
    if (camera.status !== 'ready' || captureBusyRef.current) return;
    captureBusyRef.current = true;
    const run = ++captureRunRef.current;
    const module = getCaptureModule(activeModuleId);
    const snapshot = { palette, location, faces, settings };
    try {
      setCapturePhase('countdown');
      navigate('COUNTDOWN');
      let photo: Blob | null = null;
      let frameMetadata: unknown = null;
      for (const stage of module.countdown ?? []) {
        if (captureRunRef.current !== run) return;
        setCountdown(stage);
        if (stage.effect === 'flash') {
          setCapturePhase('capturing');
          navigate('CAPTURING');
          const captured = await camera.captureFrame();
          photo = captured.blob;
          frameMetadata = captured.metadata;
        }
        await waitFor(stage.duration);
      }
      if (captureRunRef.current !== run) return;
      if (!photo) {
        const captured = await camera.captureFrame();
        photo = captured.blob;
        frameMetadata = captured.metadata;
      }
      setCountdown(null);
      setCapturePhase('processing');
      navigate('PROCESSING');
      const encounter = buildEncounter(photo, snapshot.palette, snapshot.location, snapshot.faces, snapshot.settings, { id: module.id, version: module.version });
      encounter.settingsSnapshot = { ...encounter.settingsSnapshot, captureFrame: frameMetadata };
      await module.hooks?.afterCapture?.(encounter);
      setEncounter(encounter);
      await waitFor(320);
      if (captureRunRef.current !== run) return;
      navigateWithViewTransition(() => navigate('RESULT'));
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '拍攝失敗，請再試一次。');
      navigate('CAMERA_READY');
    } finally {
      if (captureRunRef.current === run) {
        captureBusyRef.current = false;
        setCapturePhase('idle');
        setCountdown(null);
      }
    }
  }, [activeModuleId, camera, faces, location, navigate, palette, setEncounter, setNotice, settings]);

  const handleRoiPointer = (event: React.PointerEvent<HTMLElement>, mode: 'move' | 'resize') => {
    event.currentTarget.setPointerCapture(event.pointerId);
    roiStart.current = { x: event.clientX, y: event.clientY, roi: settings.roi, mode };
  };

  const moveRoi = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = roiStart.current;
    if (!start) return;
    const frame = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!frame) return;
    const dx = (event.clientX - start.x) / frame.width;
    const dy = (event.clientY - start.y) / frame.height;
    if (start.mode === 'move') updateSettings({ roi: { ...start.roi, x: Math.max(0, Math.min(1 - start.roi.width, start.roi.x + dx)), y: Math.max(0, Math.min(1 - start.roi.height, start.roi.y + dy)) } });
    else updateSettings({ roi: { ...start.roi, width: Math.max(0.2, Math.min(1 - start.roi.x, start.roi.width + dx)), height: Math.max(0.2, Math.min(1 - start.roi.y, start.roi.height + dy)) } });
  };

  const thumbUrl = useMemo(() => lastRecord ? URL.createObjectURL(lastRecord.photoBlob) : null, [lastRecord]);
  useEffect(() => () => { if (thumbUrl) URL.revokeObjectURL(thumbUrl); }, [thumbUrl]);

  const mirrored = settings.facingMode === 'user' && settings.mirrorPreview;

  return <main className="screen camera-screen" data-capture-phase={capturePhase} aria-busy={captureActive}>
    <header className="camera-toolbar">
      <button className="toolbar-button" disabled={captureActive} onClick={() => navigate('HOME')}><Icon name="back"/>返回</button>
      <button className="toolbar-button" disabled={captureActive} onClick={() => openOverlay('MODULE_SELECTOR', 'CAMERA_READY')}><Icon name="module"/>模組</button>
      <button className="toolbar-button" disabled={captureActive} onClick={() => openOverlay('CAMERA_SETTINGS', 'CAMERA_READY')}>設定<Icon name="settings"/></button>
    </header>
    <section className={`camera-viewport ${countdown?.effect ?? ''}`}>
      <video ref={camera.videoRef} playsInline muted className={settings.facingMode === 'user' && settings.mirrorPreview ? 'mirrored' : ''}/>
      <div className="camera-fallback"><span>G</span><p>CAMERA SIGNAL</p></div>
      <CameraHud faces={faces} faceStatus={faceStatus} location={location} palette={palette} showFace={settings.showFaceHud} showGps={settings.showGpsHud} showPalette={settings.showPaletteHud} sourceSize={camera.videoSize} mirrored={mirrored}/>
      {settings.colorSampling === 'region' ? <div className="roi" style={{ left: `${settings.roi.x * 100}%`, top: `${settings.roi.y * 100}%`, width: `${settings.roi.width * 100}%`, height: `${settings.roi.height * 100}%` }} onPointerDown={(event) => handleRoiPointer(event, 'move')} onPointerMove={moveRoi} onPointerUp={() => { roiStart.current = null; }}><span>COLOR ROI</span><i onPointerDown={(event) => { event.stopPropagation(); handleRoiPointer(event, 'resize'); }}/></div> : null}
      {countdown ? <div className={`countdown-layer countdown-${countdown.effect}`}>
        <CountdownEffects videoRef={camera.videoRef} effect={countdown.effect} mirrored={mirrored}/>
        <div className="countdown-matrix" aria-hidden="true">{countdownCells.map((cell) => <i key={cell}/>)}</div>
        <span className="countdown-stage mono">{countdownLabels[countdown.effect]}</span>
        <span className="countdown-axis horizontal"/><span className="countdown-axis vertical"/>
        <span className="countdown-number" key={countdown.value} data-value={countdown.value}>{countdown.value}</span>
        <span className="countdown-lock mono">FRAME / LOCK</span>
      </div> : null}
      {capturePhase === 'processing' ? <div className="processing-layer"><span className="processing-spinner"><span className="spinner"/></span><p>建立相遇紀錄</p><small className="mono">LOCAL COMPOSITE / 01</small></div> : null}
      {camera.status !== 'ready' ? <div className="camera-status-panel"><span className="status-code">CAMERA / {camera.status.toUpperCase()}</span><h2>{camera.status === 'loading' ? '正在啟動相機' : '無法使用相機'}</h2><p>{camera.error ?? '請允許相機權限後繼續。'}</p>{camera.status !== 'loading' ? <div><button className="outline-button" onClick={() => void camera.retry()}>重新嘗試</button><button className="text-button" onClick={() => navigate('HOME')}>返回首頁</button></div> : <span className="loading-line"/>}</div> : null}
    </section>
    <footer className="capture-controls">
      <button className="round-utility thumb" aria-label="開啟相簿" disabled={captureActive} onClick={() => navigate('ARCHIVE')}>{thumbUrl ? <img src={thumbUrl} alt="最近一次相遇"/> : <Icon name="archive"/>}</button>
      <button className="shutter" aria-label="拍照" disabled={camera.status !== 'ready' || captureActive} onClick={() => void runCapture()}><i/></button>
      <button className="round-utility" aria-label="切換鏡頭" disabled={captureActive} onClick={() => updateSettings({ facingMode: settings.facingMode === 'user' ? 'environment' : 'user' })}><Icon name="switch"/></button>
    </footer>
  </main>;
}
